import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { paymentsService } from '../services/paymentsService';
import { theme } from '../styles/theme';
import { formatTableDate } from '../utils/dateFormatter';

interface PaymentRow {
  orgId: string;
  smLink: string;
  firstName: string;
  lastName: string;
  amount: string;
  paymentDate: string;
}

interface BulkPaymentUploadProps {
  orgId: string;
  onUploadComplete: () => void;
  onClose: () => void;
}

const BulkPaymentUpload: React.FC<BulkPaymentUploadProps> = ({
  orgId,
  onUploadComplete,
  onClose
}) => {
  const [previewData, setPreviewData] = useState<PaymentRow[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');
  const [uploadResults, setUploadResults] = useState<{ success: number; failed: number }>({ success: 0, failed: 0 });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const requiredColumns = ['SM Link', 'First Name', 'Last Name', 'Amount', 'Payment Date'];

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      parseExcelFile(selectedFile);
    }
  };

  const parseExcelFile = (file: File) => {
    setLoading(true);
    setErrors([]);
    setPreviewData([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        // Convert to JSON with headers
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

        if (jsonData.length < 2) {
          setErrors(['File must contain at least a header row and one data row']);
          setLoading(false);
          return;
        }

        const headers = jsonData[0] as string[];
        const dataRows = jsonData.slice(1);

        // Validate headers
        const missingColumns = requiredColumns.filter(col => !headers.includes(col));
        if (missingColumns.length > 0) {
          setErrors([`Missing required columns: ${missingColumns.join(', ')}`]);
          setLoading(false);
          return;
        }

        // Get column indices
        const columnIndices = {
          smLink: headers.indexOf('SM Link'),
          firstName: headers.indexOf('First Name'),
          lastName: headers.indexOf('Last Name'),
          amount: headers.indexOf('Amount'),
          paymentDate: headers.indexOf('Payment Date')
        };

        // Parse and validate data
        const parsedData: PaymentRow[] = [];
        const validationErrors: string[] = [];

        dataRows.forEach((row, index) => {
          const rowNumber = index + 2; // +2 because of 0-index and header row

          // Skip empty rows
          if (!row || row.every(cell => !cell)) {
            return;
          }

          // Helper function to parse Excel dates
          const parseExcelDate = (value: any): string => {
            if (!value) return '';

            // If it's already a string that looks like a date, return it
            if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
              return value;
            }

            // If it's a number (Excel serial date), convert it
            if (typeof value === 'number') {
              // Excel date serial number to JavaScript Date
              const excelDate = new Date((value - 25569) * 86400 * 1000);
              if (!isNaN(excelDate.getTime())) {
                return excelDate.toISOString().split('T')[0]; // YYYY-MM-DD format
              }
            }

            // Try to parse as a regular date string
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              return date.toISOString().split('T')[0]; // YYYY-MM-DD format
            }

            // Return original value as string if nothing else works
            return value.toString();
          };

          const payment: PaymentRow = {
            orgId: orgId,
            smLink: row[columnIndices.smLink]?.toString() || '',
            firstName: row[columnIndices.firstName]?.toString() || '',
            lastName: row[columnIndices.lastName]?.toString() || '',
            amount: row[columnIndices.amount]?.toString() || '',
            paymentDate: parseExcelDate(row[columnIndices.paymentDate])
          };

          // Validate required fields
          if (!payment.smLink) {
            validationErrors.push(`Row ${rowNumber}: SM Link is required`);
          }
          if (!payment.firstName) {
            validationErrors.push(`Row ${rowNumber}: First Name is required`);
          }
          if (!payment.lastName) {
            validationErrors.push(`Row ${rowNumber}: Last Name is required`);
          }
          if (!payment.amount) {
            validationErrors.push(`Row ${rowNumber}: Amount is required`);
          } else {
            // Validate amount format
            const amountStr = payment.amount.replace(/[$,]/g, '');
            if (isNaN(parseFloat(amountStr))) {
              validationErrors.push(`Row ${rowNumber}: Amount must be a valid number`);
            }
          }
          if (!payment.paymentDate) {
            validationErrors.push(`Row ${rowNumber}: Payment Date is required`);
          } else {
            // Validate date format
            const date = new Date(payment.paymentDate);
            if (isNaN(date.getTime())) {
              validationErrors.push(`Row ${rowNumber}: Payment Date must be a valid date`);
            }
          }

          if (validationErrors.length === 0 || validationErrors.filter(e => e.includes(`Row ${rowNumber}`)).length === 0) {
            parsedData.push(payment);
          }
        });

        setErrors(validationErrors);
        setPreviewData(parsedData);
      } catch (error) {
        setErrors(['Error parsing Excel file. Please ensure it\'s a valid Excel file.']);
      }
      setLoading(false);
    };

    reader.readAsArrayBuffer(file);
  };

  const handleUpload = async () => {
    if (previewData.length === 0) {
      setErrors(['No valid data to upload']);
      return;
    }

    setUploadStatus('processing');
    let successCount = 0;
    let failedCount = 0;

    for (const payment of previewData) {
      try {
        await paymentsService.addPayment(payment);
        successCount++;
      } catch (error) {
        failedCount++;
        console.error('Failed to upload payment:', error);
      }
    }

    setUploadResults({ success: successCount, failed: failedCount });
    setUploadStatus(failedCount === 0 ? 'success' : 'error');

    if (successCount > 0) {
      onUploadComplete();
    }
  };

  const resetForm = () => {
    setPreviewData([]);
    setErrors([]);
    setUploadStatus('idle');
    setUploadResults({ success: 0, failed: 0 });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const formatDate = (date: Date) => date.toISOString().split('T')[0];

    const templateData = [
      ['SM Link', 'First Name', 'Last Name', 'Amount', 'Payment Date'],
      ['https://youtube.com/watch?v=example', 'John', 'Doe', '100.00', formatDate(yesterday)],
      ['https://youtube.com/watch?v=example2', 'Jane', 'Smith', '250.50', formatDate(today)]
    ];

    const ws = XLSX.utils.aoa_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Payment Template');
    XLSX.writeFile(wb, 'payment_upload_template.xlsx');
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        width: '100%'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '24px',
            fontWeight: '600',
            color: theme.colors.text.primary
          }}>
            Bulk Payment Upload
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              color: '#6b7280',
              cursor: 'pointer',
              padding: '0',
              lineHeight: 1
            }}
          >
            ×
          </button>
        </div>

        {/* Instructions */}
        <div style={{
          backgroundColor: '#f0f9ff',
          border: '1px solid #0ea5e9',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px'
        }}>
          <h3 style={{ margin: '0 0 8px 0', color: '#0369a1', fontSize: '16px' }}>
            Upload Instructions
          </h3>
          <ul style={{ margin: 0, paddingLeft: '20px', color: '#075985' }}>
            <li>Upload an Excel file (.xlsx) with payment data</li>
            <li>Required columns: {requiredColumns.join(', ')}</li>
            <li>Payment Date: Use Excel date format or YYYY-MM-DD (e.g., 2024-01-15)</li>
            <li>Amount can include $ and commas (e.g., $1,234.56)</li>
            <li>Download template below to see the correct format</li>
          </ul>
          <button
            onClick={downloadTemplate}
            style={{
              marginTop: '12px',
              padding: '8px 16px',
              backgroundColor: '#0ea5e9',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            Download Template
          </button>
        </div>

        {/* File Upload */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontWeight: '500',
            color: theme.colors.text.primary
          }}>
            Select Excel File
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>

        {/* Loading */}
        {loading && (
          <div style={{
            textAlign: 'center',
            padding: '20px',
            color: theme.colors.text.secondary
          }}>
            Parsing file...
          </div>
        )}

        {/* Errors */}
        {errors.length > 0 && (
          <div style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fca5a5',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#dc2626' }}>
              Validation Errors:
            </h4>
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#dc2626' }}>
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Preview Data */}
        {previewData.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{
              marginBottom: '12px',
              fontSize: '18px',
              color: theme.colors.text.primary
            }}>
              Preview ({previewData.length} payments)
            </h3>
            <div style={{
              maxHeight: '300px',
              overflow: 'auto',
              border: '1px solid #d1d5db',
              borderRadius: '6px'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '500' }}>
                      Name
                    </th>
                    <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '500' }}>
                      Amount
                    </th>
                    <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '500' }}>
                      Date
                    </th>
                    <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '500' }}>
                      SM Link
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.slice(0, 10).map((payment, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '8px', fontSize: '14px' }}>
                        {payment.firstName} {payment.lastName}
                      </td>
                      <td style={{ padding: '8px', fontSize: '14px' }}>
                        {payment.amount}
                      </td>
                      <td style={{ padding: '8px', fontSize: '14px' }}>
                        {formatTableDate(payment.paymentDate)}
                      </td>
                      <td style={{ padding: '8px', fontSize: '14px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {payment.smLink}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {previewData.length > 10 && (
                <div style={{ padding: '8px', textAlign: 'center', fontSize: '12px', color: '#6b7280' }}>
                  ... and {previewData.length - 10} more payments
                </div>
              )}
            </div>
          </div>
        )}

        {/* Upload Status */}
        {uploadStatus === 'processing' && (
          <div style={{
            textAlign: 'center',
            padding: '20px',
            color: theme.colors.text.secondary
          }}>
            Uploading payments...
          </div>
        )}

        {uploadStatus === 'success' && (
          <div style={{
            backgroundColor: '#f0fdf4',
            border: '1px solid #22c55e',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#16a34a' }}>
              Upload Successful!
            </h4>
            <p style={{ margin: 0, color: '#16a34a' }}>
              {uploadResults.success} payments uploaded successfully
            </p>
          </div>
        )}

        {uploadStatus === 'error' && (
          <div style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #ef4444',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#dc2626' }}>
              Upload Completed with Errors
            </h4>
            <p style={{ margin: 0, color: '#dc2626' }}>
              {uploadResults.success} successful, {uploadResults.failed} failed
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          {uploadStatus === 'idle' && (
            <>
              <button
                onClick={onClose}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={previewData.length === 0 || errors.length > 0}
                style={{
                  padding: '12px 24px',
                  backgroundColor: previewData.length > 0 && errors.length === 0 ? theme.colors.primary : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: previewData.length > 0 && errors.length === 0 ? 'pointer' : 'not-allowed'
                }}
              >
                Upload {previewData.length} Payments
              </button>
            </>
          )}

          {(uploadStatus === 'success' || uploadStatus === 'error') && (
            <>
              <button
                onClick={resetForm}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Upload More
              </button>
              <button
                onClick={onClose}
                style={{
                  padding: '12px 24px',
                  backgroundColor: theme.colors.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkPaymentUpload;